import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton, BannerItemsType } from '../stories/molecules';
import { isFailureResponse } from '../utils';
import { ErrorType, logError } from '../utils/splunkLogger';
import { homeTranslations } from '../translations';
import { useStores } from './useStores';

const OFFLINE_BANNER_ID = 'offline-banner';

export const useOfflineBanner = (setSystemBanners: React.Dispatch<React.SetStateAction<BannerItemsType[]>>) => {
  const { pingStores } = useStores();
  const { t } = useTranslation();

  const checkBackendConnectivity = useCallback(async (): Promise<boolean> => {
    const response = await pingStores();
    if (isFailureResponse(response)) {
      logError({
        error: new Error('Failed to ping backend'),
        errorType: ErrorType.AUTH_ERROR,
        info: { message: 'Failed to ping backend' },
      });
      return false;
    }
    return true;
  }, [pingStores]);

  const removeOfflineBanner = useCallback(() => {
    setSystemBanners((prev) => prev.filter((b) => b.id !== OFFLINE_BANNER_ID));
  }, [setSystemBanners]);

  const showOfflineBanner = useCallback(() => {
    setSystemBanners((prev) => {
      if (prev.some((b) => b.id === OFFLINE_BANNER_ID)) return prev;
      return [
        ...prev,
        {
          id: OFFLINE_BANNER_ID,
          label: t(homeTranslations.banners.offline.key, homeTranslations.banners.offline.defaultValue),
          type: 'warning',
          actionButton: ActionButton.TryAgain,
          onTryAgain: async () => {
            const reachable = await checkBackendConnectivity();
            if (reachable) removeOfflineBanner();
          },
        },
      ];
    });
  }, [setSystemBanners, t, checkBackendConnectivity, removeOfflineBanner]);

  useEffect(() => {
    const handleOnline = async () => {
      const reachable = await checkBackendConnectivity();
      if (reachable) {
        removeOfflineBanner();
      } else {
        showOfflineBanner();
      }
    };

    // Initial check
    checkBackendConnectivity().then((reachable) => {
      if (reachable) {
        removeOfflineBanner();
      } else {
        showOfflineBanner();
      }
    });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', showOfflineBanner);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', showOfflineBanner);
    };
  }, []);
};
